import { DictionaryManager } from './dictionary';
import { PERFORMANCE, TYPING, CONSISTENCY, WPM_TRACKING, REFILL } from './constants';
/**
 * Core Typing Engine
 *
 * Handles keystroke processing, state management, and calculations.
 * Explicit phase FSM — input accepted only while Racing.
 */
export class TypingEngine {
    constructor() {
        this.session = null;
        this.keystrokeHistory = [];
        this.lastKeystrokeTime = 0;
        this.eventListeners = new Map();
        this.timeModeInterval = null;
        this.totalMistakes = 0;
        this.wpmSamples = [];
        this.lastWpmSampleTime = 0;
        this.pauseStartTime = null;
        this.totalPausedTime = 0;
        this.pendingKeystrokes = new Map();
        this.flatChars = [];
        this.activeCharIndex = 0;
        this.phase = 'idle';
        this.regenerating = false;
        this.refillRequested = false;
        this.initGeneration = 0;
        this.softNotice = null;
        this.dictionaryManager = new DictionaryManager();
    }
    clearDictionaryCache() {
        this.dictionaryManager.clearCache();
    }
    getPhase() {
        return this.phase;
    }
    getSoftNotice() {
        return this.softNotice;
    }
    clearSoftNotice() {
        this.softNotice = null;
    }
    /** Pause-aware elapsed seconds (excludes paused time). */
    getActiveElapsedSeconds(atTime = Date.now()) {
        if (!this.session)
            return 0;
        const activePaused = this.session.isPaused && this.pauseStartTime
            ? atTime - this.pauseStartTime
            : 0;
        return Math.max(0, (atTime - this.session.startTime - this.totalPausedTime - activePaused) / 1000);
    }
    getTotalPausedTimeMs() {
        const activePaused = this.session?.isPaused && this.pauseStartTime
            ? Date.now() - this.pauseStartTime
            : 0;
        return this.totalPausedTime + activePaused;
    }
    /**
     * Initialize a new typing session. Returns null if a newer init superseded this one.
     */
    async initializeSession(config, forceRefreshDictionary = false) {
        const generation = ++this.initGeneration;
        this.setPhase('loading');
        this.softNotice = null;
        try {
            const dictionary = await this.dictionaryManager.loadDictionary(config.dictionary, forceRefreshDictionary);
            if (generation !== this.initGeneration) {
                return null;
            }
            const { words, notice } = this.generateWords(config, dictionary);
            if (notice) {
                this.softNotice = notice;
            }
            if (words.length === 0) {
                this.softFail('empty_words', 'Sõnastik on tühi. Proovi teist raskusastet.', true);
                return null;
            }
            this.session = {
                words,
                config,
                startTime: Date.now(),
                activeWordIndex: 0,
                activeLetterIndex: 0,
                isActive: false,
                isPaused: false,
                sessionId: this.generateSessionId(),
            };
            this.keystrokeHistory = [];
            this.lastKeystrokeTime = 0;
            this.totalMistakes = 0;
            this.wpmSamples = [];
            this.lastWpmSampleTime = 0;
            this.pauseStartTime = null;
            this.totalPausedTime = 0;
            this.pendingKeystrokes.clear();
            this.regenerating = false;
            this.refillRequested = false;
            this.rebuildFlatCharsFromWords();
            this.setPhase('ready');
            this.emit('session_initialized', this.session);
            return this.session;
        }
        catch (error) {
            if (generation !== this.initGeneration) {
                return null;
            }
            console.error('Failed to initialize session:', error);
            this.softFail('dictionary_load', 'Sõnastiku laadimine ebaõnnestus. Proovi uuesti.', true);
            return null;
        }
    }
    setPhase(phase) {
        this.phase = phase;
        this.emit('phase_change', { phase });
    }
    softFail(code, message, recoverable = true) {
        this.setPhase('soft_error');
        if (this.session) {
            this.session.isActive = false;
            this.session.isPaused = false;
        }
        if (this.timeModeInterval) {
            clearTimeout(this.timeModeInterval);
            this.timeModeInterval = null;
        }
        const payload = { code, message, recoverable };
        this.emit('soft_fail', payload);
        this.emit('error', payload);
    }
    rebuildFlatCharsFromWords() {
        if (!this.session)
            return;
        const flat = [];
        this.session.words.forEach((word, wIndex) => {
            const text = word.originalText;
            for (let i = 0; i < text.length; i++) {
                const letter = word.letters[i];
                flat.push({
                    char: text[i],
                    state: letter ? letter.state : 'neutral',
                    wordIndex: wIndex,
                    letterIndexInWord: i,
                    isSpace: false,
                    id: `${word.id}-char-${i}`,
                    timestamp: letter?.timestamp,
                });
            }
            if (wIndex < this.session.words.length - 1) {
                flat.push({
                    char: ' ',
                    state: 'neutral',
                    wordIndex: wIndex,
                    letterIndexInWord: null,
                    isSpace: true,
                    id: `${word.id}-space`,
                });
            }
        });
        this.flatChars = flat;
        this.activeCharIndex = 0;
    }
    /**
     * Append flat-stream entries for words starting at startWordIndex.
     * Preserves typed history and activeCharIndex (points at first new char).
     */
    appendFlatCharsFromWords(startWordIndex) {
        if (!this.session || startWordIndex >= this.session.words.length)
            return;
        const hadPriorWords = startWordIndex > 0;
        const firstNewCharIndex = this.flatChars.length;
        // Bridge space between previous last word and newly appended words
        if (hadPriorWords) {
            const prevWord = this.session.words[startWordIndex - 1];
            const bridgeAlreadyExists = this.flatChars.length > 0 &&
                this.flatChars[this.flatChars.length - 1].isSpace &&
                this.flatChars[this.flatChars.length - 1].wordIndex === startWordIndex - 1;
            if (!bridgeAlreadyExists) {
                this.flatChars.push({
                    char: ' ',
                    state: 'neutral',
                    wordIndex: startWordIndex - 1,
                    letterIndexInWord: null,
                    isSpace: true,
                    id: `${prevWord.id}-space`,
                });
            }
        }
        const lastIndex = this.session.words.length - 1;
        for (let wIndex = startWordIndex; wIndex <= lastIndex; wIndex++) {
            const word = this.session.words[wIndex];
            const text = word.originalText;
            for (let i = 0; i < text.length; i++) {
                const letter = word.letters[i];
                this.flatChars.push({
                    char: text[i],
                    state: letter ? letter.state : 'neutral',
                    wordIndex: wIndex,
                    letterIndexInWord: i,
                    isSpace: false,
                    id: `${word.id}-char-${i}`,
                    timestamp: letter?.timestamp,
                });
            }
            if (wIndex < lastIndex) {
                this.flatChars.push({
                    char: ' ',
                    state: 'neutral',
                    wordIndex: wIndex,
                    letterIndexInWord: null,
                    isSpace: true,
                    id: `${word.id}-space`,
                });
            }
        }
        // If the typist was stuck at end-of-stream, land on the bridge/first new char
        if (this.activeCharIndex >= firstNewCharIndex) {
            this.activeCharIndex = firstNewCharIndex;
        }
    }
    /**
     * Process a keystroke. Accepted only while phase is Racing (not paused/regenerating).
     */
    processKeystroke(key, timestamp = Date.now()) {
        if (!this.session || this.phase !== 'racing' || this.session.isPaused) {
            return;
        }
        const modifierKeys = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape'];
        if (modifierKeys.includes(key)) {
            return;
        }
        // Reject multi-char inserts (paste/autofill bursts)
        if (key.length > 1 && key !== 'Backspace') {
            return;
        }
        const startTime = performance.now();
        try {
            if (key === 'Backspace') {
                this.handleBackspace(timestamp);
                this.emit('keystroke', { key, session: this.session });
                return;
            }
            if (!this.session)
                return;
            const currentWord = this.session.words[this.session.activeWordIndex];
            if (!currentWord)
                return;
            const entry = this.flatChars[this.activeCharIndex];
            if (!entry) {
                // Beyond end of stream while regenerating — drop safely
                return;
            }
            const normalizedKey = this.normalizeCharacter(key);
            const expectedKey = this.normalizeCharacter(entry.char);
            const isCorrect = normalizedKey === expectedKey;
            if (!entry.isSpace) {
                const word = this.session.words[entry.wordIndex];
                if (word && !word.startTime && entry.letterIndexInWord === 0) {
                    word.startTime = timestamp;
                }
            }
            entry.state = isCorrect ? 'correct' : 'incorrect';
            entry.timestamp = timestamp;
            if (!entry.isSpace && entry.letterIndexInWord !== null) {
                const word = this.session.words[entry.wordIndex];
                const letter = word?.letters[entry.letterIndexInWord];
                if (letter) {
                    letter.state = entry.state;
                    letter.timestamp = timestamp;
                }
                if (!isCorrect && word) {
                    word.errorCount++;
                    this.totalMistakes++;
                }
            }
            else if (entry.isSpace && !isCorrect) {
                this.totalMistakes++;
            }
            if (!entry.isSpace) {
                this.session.activeWordIndex = entry.wordIndex;
                this.session.activeLetterIndex = (entry.letterIndexInWord ?? 0) + 1;
            }
            else {
                const nextWordIndex = entry.wordIndex + 1;
                if (nextWordIndex < this.session.words.length) {
                    this.session.activeWordIndex = nextWordIndex;
                    this.session.activeLetterIndex = 0;
                }
            }
            this.activeCharIndex++;
            const telemetry = this.recordKeystroke(key, expectedKey, timestamp, isCorrect);
            const keystrokeId = `${key}-${timestamp}-${this.session.activeWordIndex}-${this.session.activeLetterIndex}`;
            this.pendingKeystrokes.set(keystrokeId, telemetry);
            this.keystrokeHistory.push(telemetry);
            if (entry.isSpace) {
                const wordJustFinished = this.session.words[entry.wordIndex];
                const allValidated = wordJustFinished.letters.length > 0 &&
                    wordJustFinished.letters.every((l) => l.state !== 'neutral');
                if (allValidated) {
                    this.session.activeWordIndex = entry.wordIndex;
                    this.session.activeLetterIndex = wordJustFinished.letters.length;
                    this.checkAndHandleWordCompletion();
                }
            }
            else if (this.activeCharIndex >= this.flatChars.length) {
                // Last character of last word (no trailing space) — complete the word
                this.checkAndHandleWordCompletion();
            }
            this.emit('keystroke', { key, session: this.session });
            const processingTime = performance.now() - startTime;
            if (processingTime > PERFORMANCE.MAX_KEYSTROKE_PROCESSING_TIME_MS) {
                console.warn(`Keystroke processing took ${processingTime.toFixed(2)}ms`);
            }
            this.trackWpmSample();
            this.maybePrefetchWords();
        }
        catch (error) {
            // Soft: ignore bad keystroke, keep racing
            console.error('Error processing keystroke:', error);
            this.emit('error', {
                code: 'keystroke',
                message: 'Keystroke ignored',
                recoverable: true,
                error,
            });
        }
    }
    calculateStats() {
        if (!this.session) {
            return this.createEmptyStats();
        }
        const duration = this.getActiveElapsedSeconds();
        const durationMinutes = duration / 60;
        let charactersTyped = 0;
        let charactersCorrect = 0;
        let totalKeystrokes = 0;
        let correctKeystrokes = 0;
        this.session.words.forEach((word) => {
            word.letters.forEach((letter) => {
                if (letter.state === 'neutral')
                    return;
                totalKeystrokes++;
                charactersTyped++;
                if (letter.state === 'correct') {
                    correctKeystrokes++;
                    charactersCorrect++;
                }
            });
        });
        this.keystrokeHistory.forEach((keystroke) => {
            if (keystroke.isSpacebar && !keystroke.isBackspace) {
                totalKeystrokes++;
                charactersTyped++;
                if (keystroke.isCorrect) {
                    correctKeystrokes++;
                    charactersCorrect++;
                }
            }
        });
        let errors = this.totalMistakes;
        if (errors === 0 && this.keystrokeHistory.length > 0) {
            errors = this.keystrokeHistory.filter((k) => !k.isCorrect && !k.isBackspace).length;
        }
        const wpm = durationMinutes > 0
            ? charactersCorrect / TYPING.CHARACTERS_PER_WORD / durationMinutes
            : 0;
        const rawWpm = durationMinutes > 0
            ? charactersTyped / TYPING.CHARACTERS_PER_WORD / durationMinutes
            : 0;
        const accuracy = totalKeystrokes > 0 ? (correctKeystrokes / totalKeystrokes) * 100 : 0;
        const consistency = this.calculateConsistency();
        const peakWpm = this.wpmSamples.length > 0
            ? Math.max(...this.wpmSamples.map((s) => s.wpm))
            : wpm;
        const wordsCompleted = this.session.words.filter((w) => w.isComplete).length;
        return {
            wpm,
            rawWpm,
            accuracy,
            consistency,
            errors,
            totalKeystrokes,
            correctKeystrokes,
            charactersTyped,
            charactersCorrect,
            duration,
            averageWordWpm: 0,
            peakWpm,
            wpmOverTime: this.wpmSamples.map((s) => ({ time: s.timestamp, wpm: s.wpm })),
            wordsCompleted,
        };
    }
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event).push(callback);
    }
    off(event, callback) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }
    emit(event, data) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach((callback) => {
                try {
                    callback(data);
                }
                catch (err) {
                    console.error(`Listener error for ${event}:`, err);
                }
            });
        }
    }
    getSession() {
        return this.session;
    }
    getKeystrokeHistory() {
        return [...this.keystrokeHistory];
    }
    processKeyup(key, timestamp = Date.now()) {
        if (!this.session || (this.phase !== 'racing' && this.phase !== 'paused'))
            return;
        const modifierKeys = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape'];
        if (modifierKeys.includes(key)) {
            return;
        }
        let matchedTelemetry = null;
        for (let i = this.keystrokeHistory.length - 1; i >= 0; i--) {
            const telemetry = this.keystrokeHistory[i];
            if (telemetry.key === key &&
                telemetry.keyupTime === telemetry.keydownTime &&
                timestamp - telemetry.keydownTime < 5000) {
                matchedTelemetry = telemetry;
                break;
            }
        }
        if (matchedTelemetry) {
            matchedTelemetry.keyupTime = timestamp;
            matchedTelemetry.duration = timestamp - matchedTelemetry.keydownTime;
            if (this.session && matchedTelemetry.wordIndex < this.session.words.length) {
                const word = this.session.words[matchedTelemetry.wordIndex];
                if (word && matchedTelemetry.letterIndex < word.letters.length) {
                    const letter = word.letters[matchedTelemetry.letterIndex];
                    if (letter) {
                        letter.keyDuration = matchedTelemetry.duration;
                    }
                }
            }
            for (const [id, pending] of this.pendingKeystrokes.entries()) {
                if (pending === matchedTelemetry) {
                    this.pendingKeystrokes.delete(id);
                    break;
                }
            }
        }
    }
    startTest() {
        if (!this.session)
            return;
        if (this.phase === 'racing')
            return;
        if (this.phase !== 'ready')
            return;
        this.session.isActive = true;
        this.session.isPaused = false;
        this.session.startTime = Date.now();
        this.totalPausedTime = 0;
        this.pauseStartTime = null;
        this.setPhase('racing');
        this.emit('test_start', this.session);
        if (this.session.config.mode === 'time' && this.session.config.duration) {
            this.timeModeInterval = window.setTimeout(() => {
                this.endTest();
            }, this.session.config.duration * 1000);
        }
    }
    endTest() {
        if (!this.session)
            return;
        if (this.phase === 'finished')
            return;
        this.session.isActive = false;
        this.session.isPaused = false;
        this.session.endTime = Date.now();
        if (this.timeModeInterval) {
            clearTimeout(this.timeModeInterval);
            this.timeModeInterval = null;
        }
        this.regenerating = false;
        this.setPhase('finished');
        this.emit('test_complete', this.session);
    }
    resetTest() {
        this.initGeneration++;
        if (this.timeModeInterval) {
            clearTimeout(this.timeModeInterval);
            this.timeModeInterval = null;
        }
        this.session = null;
        this.keystrokeHistory = [];
        this.lastKeystrokeTime = 0;
        this.totalMistakes = 0;
        this.wpmSamples = [];
        this.lastWpmSampleTime = 0;
        this.pauseStartTime = null;
        this.totalPausedTime = 0;
        this.pendingKeystrokes.clear();
        this.flatChars = [];
        this.activeCharIndex = 0;
        this.regenerating = false;
        this.refillRequested = false;
        this.softNotice = null;
        this.setPhase('idle');
        this.emit('test_reset', null);
    }
    pauseTest() {
        if (!this.session || this.phase !== 'racing' || this.session.isPaused)
            return;
        this.session.isPaused = true;
        this.pauseStartTime = Date.now();
        if (this.timeModeInterval) {
            clearTimeout(this.timeModeInterval);
            this.timeModeInterval = null;
        }
        this.setPhase('paused');
        this.emit('test_paused', this.session);
    }
    resumeTest() {
        if (!this.session || this.phase !== 'paused' || !this.session.isPaused)
            return;
        this.session.isPaused = false;
        if (this.pauseStartTime) {
            this.totalPausedTime += Date.now() - this.pauseStartTime;
            this.pauseStartTime = null;
        }
        if (this.session.config.mode === 'time' && this.session.config.duration) {
            const elapsed = this.getActiveElapsedSeconds();
            const remaining = this.session.config.duration - elapsed;
            if (remaining > 0) {
                this.timeModeInterval = window.setTimeout(() => {
                    this.endTest();
                }, remaining * 1000);
            }
            else {
                this.endTest();
                return;
            }
        }
        this.setPhase('racing');
        this.emit('test_resumed', this.session);
    }
    trackWpmSample() {
        if (!this.session || this.phase !== 'racing')
            return;
        const now = Date.now();
        const timeSinceLastSample = now - this.lastWpmSampleTime;
        if (timeSinceLastSample >= WPM_TRACKING.SAMPLE_INTERVAL_MS) {
            const stats = this.calculateStats();
            this.wpmSamples.push({
                timestamp: now - this.session.startTime - this.totalPausedTime,
                wpm: stats.wpm,
            });
            this.lastWpmSampleTime = now;
        }
    }
    getWpmSamples() {
        return [...this.wpmSamples];
    }
    /**
     * Prefetch more words in time mode before the stream runs out.
     */
    maybePrefetchWords() {
        if (!this.session || this.session.config.mode !== 'time')
            return;
        if (this.regenerating || this.refillRequested || this.phase !== 'racing')
            return;
        const remainingWords = this.session.words.length - this.session.activeWordIndex - 1;
        if (remainingWords <= REFILL.PREFETCH_REMAINING_WORDS) {
            this.refillRequested = true;
            this.emit('words_exhausted', this.session);
        }
    }
    /**
     * Generate new words and append to the flat stream atomically.
     * Preserves timer and typed history.
     */
    async generateMoreWords(forceRefreshDictionary = false) {
        if (!this.session)
            return false;
        if (this.session.config.mode !== 'time')
            return false;
        if (this.regenerating)
            return false;
        // Don't refill after the race ended
        if (this.phase === 'finished' || this.phase === 'idle' || this.phase === 'soft_error') {
            return false;
        }
        this.regenerating = true;
        this.refillRequested = true;
        // Keep phase as racing/paused so typing isn't blocked while refill is in flight.
        try {
            const dictionary = await this.dictionaryManager.loadDictionary(this.session.config.dictionary, forceRefreshDictionary);
            // Re-read phase after await — race may have ended meanwhile
            const phaseAfterLoad = this.getPhase();
            if (!this.session ||
                phaseAfterLoad === 'finished' ||
                phaseAfterLoad === 'idle' ||
                phaseAfterLoad === 'soft_error') {
                this.regenerating = false;
                this.refillRequested = false;
                return false;
            }
            const refillConfig = {
                ...this.session.config,
                mode: 'time',
                duration: Math.ceil((REFILL.BATCH_WORD_COUNT / TYPING.ESTIMATED_WPM_FOR_TIME_MODE) * 60),
            };
            const { words: newWords, notice } = this.generateWords(refillConfig, dictionary);
            if (notice) {
                this.softNotice = notice;
            }
            if (newWords.length === 0) {
                this.regenerating = false;
                this.refillRequested = false;
                this.emit('error', {
                    code: 'refill_empty',
                    message: 'Could not generate more words',
                    recoverable: true,
                });
                return false;
            }
            const startWordIndex = this.session.words.length;
            const priorWordCompleted = startWordIndex > 0 &&
                !!this.session.words[startWordIndex - 1]?.isComplete;
            newWords.forEach((word, index) => {
                word.isComplete = false;
                word.isActive = false;
                word.errorCount = 0;
                const globalIndex = startWordIndex + index;
                word.id = `word-${globalIndex}`;
                word.letters.forEach((letter, letterIndex) => {
                    letter.id = `word-${globalIndex}-letter-${letterIndex}`;
                    letter.state = 'neutral';
                    letter.timestamp = undefined;
                });
            });
            this.session.words.push(...newWords);
            const streamLenBefore = this.flatChars.length;
            this.appendFlatCharsFromWords(startWordIndex);
            // If prior word finished at end-of-stream (no trailing space typed),
            // skip the bridge space and land on the first letter of the new batch.
            if (priorWordCompleted && this.flatChars.length > streamLenBefore) {
                const bridgeIndex = streamLenBefore;
                if (this.flatChars[bridgeIndex]?.isSpace) {
                    this.flatChars[bridgeIndex].state = 'correct';
                    this.activeCharIndex = bridgeIndex + 1;
                    this.session.activeWordIndex = startWordIndex;
                    this.session.activeLetterIndex = 0;
                }
            }
            else if (this.activeCharIndex < this.flatChars.length &&
                this.flatChars[this.activeCharIndex]) {
                const entry = this.flatChars[this.activeCharIndex];
                this.session.activeWordIndex = entry.wordIndex;
                this.session.activeLetterIndex = entry.isSpace
                    ? 0
                    : entry.letterIndexInWord ?? 0;
            }
            if (this.session.words[this.session.activeWordIndex]) {
                this.session.words.forEach((w, i) => {
                    w.isActive = i === this.session.activeWordIndex;
                });
            }
            this.regenerating = false;
            this.refillRequested = false;
            this.emit('words_appended', this.session);
            return true;
        }
        catch (error) {
            console.error('Failed to generate more words:', error);
            this.regenerating = false;
            this.refillRequested = false;
            this.emit('error', {
                code: 'refill_failed',
                message: 'Sõnade juurde laadimine ebaõnnestus',
                recoverable: true,
                error,
            });
            return false;
        }
    }
    createEmptyStats() {
        return {
            wpm: 0,
            rawWpm: 0,
            accuracy: 0,
            consistency: 0,
            errors: 0,
            totalKeystrokes: 0,
            correctKeystrokes: 0,
            charactersTyped: 0,
            charactersCorrect: 0,
            duration: 0,
            averageWordWpm: 0,
            peakWpm: 0,
            wpmOverTime: [],
            wordsCompleted: 0,
        };
    }
    generateSessionId() {
        return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    calculateConsistency() {
        if (this.keystrokeHistory.length < 2)
            return 100;
        const spacings = this.keystrokeHistory
            .filter((k) => !k.isBackspace)
            .map((k) => k.spacing)
            .filter((s) => s > 0 && s < CONSISTENCY.MAX_SPACING_MS);
        if (spacings.length < 2)
            return 100;
        const avg = spacings.reduce((a, b) => a + b, 0) / spacings.length;
        const variance = spacings.reduce((sum, s) => sum + Math.pow(s - avg, 2), 0) / spacings.length;
        const stdDev = Math.sqrt(variance);
        const coefficientOfVariation = avg > 0 ? stdDev / avg : 0;
        return Math.max(0, Math.min(100, (1 - coefficientOfVariation) * 100));
    }
    /**
     * Normalize character for keyboard compatibility (input and expected).
     */
    normalizeCharacter(char) {
        if (!char || char.length === 0)
            return char;
        let normalized = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        normalized = normalized.replace(/['']/g, "'");
        normalized = normalized.replace(/[""]/g, '"');
        normalized = normalized.replace(/[—–]/g, '-');
        normalized = normalized.replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ');
        normalized = normalized.replace(/[\u200B-\u200D\uFEFF]/g, '');
        normalized = normalized.replace(/[ﬁ]/g, 'fi');
        normalized = normalized.replace(/[ﬂ]/g, 'fl');
        normalized = normalized.replace(/[ﬀ]/g, 'ff');
        normalized = normalized.replace(/[ﬃ]/g, 'ffi');
        normalized = normalized.replace(/[ﬄ]/g, 'ffl');
        return normalized;
    }
    handleBackspace(timestamp) {
        if (!this.session)
            return;
        if (this.activeCharIndex === 0) {
            const telemetry = this.recordKeystroke('Backspace', '', timestamp, false);
            this.keystrokeHistory.push(telemetry);
            return;
        }
        // Don't allow backspacing into a completed word's prior content across a finished boundary
        // Flat-stream model: simply step back one char
        this.activeCharIndex--;
        const entry = this.flatChars[this.activeCharIndex];
        if (!entry) {
            this.activeCharIndex++;
            return;
        }
        // If the word was marked complete (via space), un-complete when backspacing onto/into it
        const word = this.session.words[entry.wordIndex];
        if (word?.isComplete) {
            word.isComplete = false;
            word.isActive = true;
            // Deactivate any later "active" word
            this.session.words.forEach((w, i) => {
                if (i !== entry.wordIndex)
                    w.isActive = false;
            });
        }
        entry.state = 'neutral';
        entry.timestamp = undefined;
        if (!entry.isSpace && entry.letterIndexInWord !== null) {
            const letter = word?.letters[entry.letterIndexInWord];
            if (letter) {
                letter.state = 'neutral';
                letter.timestamp = undefined;
            }
        }
        if (entry.isSpace) {
            this.session.activeWordIndex = entry.wordIndex;
            this.session.activeLetterIndex =
                this.session.words[entry.wordIndex].letters.length;
        }
        else {
            this.session.activeWordIndex = entry.wordIndex;
            this.session.activeLetterIndex = entry.letterIndexInWord ?? 0;
        }
        const telemetry = this.recordKeystroke('Backspace', '', timestamp, false);
        this.keystrokeHistory.push(telemetry);
    }
    checkAndHandleWordCompletion() {
        if (!this.session)
            return;
        const currentWord = this.session.words[this.session.activeWordIndex];
        if (!currentWord)
            return;
        const isComplete = this.session.activeLetterIndex >= currentWord.letters.length;
        if (!isComplete)
            return;
        if (currentWord.letters.length > 0) {
            const lastLetter = currentWord.letters[currentWord.letters.length - 1];
            if (lastLetter.state === 'neutral')
                return;
            if (this.session.activeLetterIndex !== currentWord.letters.length)
                return;
        }
        this.handleWordCompletion();
    }
    handleWordCompletion() {
        if (!this.session)
            return;
        const currentWord = this.session.words[this.session.activeWordIndex];
        if (!currentWord || currentWord.isComplete)
            return;
        const now = Date.now();
        const wordStartTime = currentWord.startTime || this.session.startTime;
        currentWord.completionTime = now - wordStartTime;
        const originalLength = currentWord.originalText.length;
        const correctChars = currentWord.letters
            .slice(0, originalLength)
            .filter((letter) => letter.state === 'correct').length;
        if (currentWord.completionTime > 0) {
            const timeInMinutes = currentWord.completionTime / 60000;
            currentWord.wpm =
                timeInMinutes > 0 ? correctChars / 5 / timeInMinutes : 0;
        }
        else {
            currentWord.wpm = 0;
        }
        currentWord.isComplete = true;
        currentWord.isActive = false;
        const completedWords = this.session.words.filter((w) => w.isComplete).length;
        if (this.session.activeWordIndex < this.session.words.length - 1) {
            this.session.activeWordIndex++;
            this.session.activeLetterIndex = 0;
            const nextWord = this.session.words[this.session.activeWordIndex];
            nextWord.isActive = true;
            // Words mode: finish when target count reached
            if (this.session.config.mode === 'words' &&
                this.session.config.wordCount &&
                completedWords >= this.session.config.wordCount) {
                this.endTest();
                return;
            }
        }
        else {
            // Reached end of current word pool
            if (this.session.config.mode === 'time') {
                if (!this.refillRequested && !this.regenerating) {
                    this.refillRequested = true;
                    this.emit('words_exhausted', this.session);
                }
            }
            else {
                // Words and paragraphs: test complete
                this.endTest();
            }
        }
    }
    recordKeystroke(key, expectedKey, timestamp, isCorrect) {
        const spacing = timestamp - this.lastKeystrokeTime;
        this.lastKeystrokeTime = timestamp;
        return {
            key,
            expectedKey,
            keydownTime: timestamp,
            keyupTime: timestamp,
            duration: 0,
            spacing,
            isCorrect,
            wordIndex: this.session?.activeWordIndex || 0,
            letterIndex: this.session?.activeLetterIndex || 0,
            isBackspace: key === 'Backspace',
            isSpacebar: key === ' ',
        };
    }
    generateWords(config, dictionary) {
        const words = [];
        let wordCount = 50;
        let notice = null;
        if (config.mode === 'words' && config.wordCount) {
            wordCount = config.wordCount;
        }
        else if (config.mode === 'paragraphs' && config.wordCount) {
            wordCount = config.wordCount;
        }
        else if (config.mode === 'time' && config.duration) {
            wordCount = Math.ceil((config.duration / 60) * TYPING.ESTIMATED_WPM_FOR_TIME_MODE);
            // Ensure enough buffer for fast typists
            wordCount = Math.max(wordCount, REFILL.BATCH_WORD_COUNT);
        }
        const isParagraphs = config.mode === 'paragraphs';
        const result = this.dictionaryManager.generateWordArray(wordCount, dictionary, config.difficulty, isParagraphs);
        notice = result.notice;
        const wordArray = result.words;
        wordArray.forEach((wordText, index) => {
            const letters = wordText.split('').map((char, charIndex) => ({
                char,
                state: 'neutral',
                id: `word-${index}-letter-${charIndex}`,
            }));
            words.push({
                letters,
                isComplete: false,
                isActive: index === 0,
                errorCount: 0,
                originalText: wordText,
                id: `word-${index}`,
            });
        });
        return { words, notice };
    }
}
